import { useTheme } from '@emotion/react'
import {
    AppState,
    entitledToViewContent,
    Issue,
    PremiumPublicationsSet,
} from '@news-mono/web-common'
import React from 'react'
import { useSelector } from 'react-redux'
import { useAuthActions } from '../../user-registration'
import { useSection } from '../../__helpers'
import { useProduct } from '../../__product'
import { calcRem, colors } from '../../__styling'
import {
    StyledEPaperArrow,
    StyledEPaperBanner,
    StyledEPaperBannerWrapper,
    StyledEPaperButton,
    StyledEPaperChevon,
    StyledEPaperCoffee,
    StyledEPaperCoverLeft,
    StyledEPaperCoverRight,
    StyledEPaperDateTime,
    StyledEPaperHeadingEpaper,
    StyledEPaperHeadingToday,
    StyledEPaperHeadingWrapper,
    StyledEPaperLink,
    StyledEPaperLogo,
    StyledEPaperLogoWrapper,
    StyledEPaperNewspaper,
    StyledEPaperPNButton,
    StyledEPaperSubheading,
    StyledEpaperSubscribeButton,
    StyledEpaperSubscribeText,
    StyledEpaperSubscribeUnderlineText,
    StyledEpaperUnsubscribedBanner,
} from './ePaperPage.styled'

export type PNRegionalSections =
    | 'joondalup'
    | 'wanneroo'
    | 'stirling'
    | 'western-suburbs'
    | 'central'
    | 'southern'
    | 'canning'
    | 'cockburn'
    | 'fremantle'
    | 'melville'
    | 'mandurah-coastal-times'

export type ePaperRegions =
    | 'North'
    | 'South'
    | 'Central'
    | 'Mandurah'
    | 'default'

export interface ePaperPageProps {
    latestEdition: Issue
    allEditions: Issue[]
    onEvent: (event: any) => void
    regionalSection?: PNRegionalSections
}

export function selectPNRegion(publication: PNRegionalSections | undefined): {
    section: ePaperRegions
    color: string
} {
    if (
        publication === 'joondalup' ||
        publication === 'wanneroo' ||
        publication === 'stirling'
    ) {
        return { section: 'North', color: colors.perthnow.pinkThulite }
    }
    if (
        publication === 'western-suburbs' ||
        publication === 'central' ||
        publication === 'southern'
    ) {
        return { section: 'Central', color: colors.perthnow.purpleAmethyst }
    }
    if (
        publication === 'canning' ||
        publication === 'cockburn' ||
        publication === 'fremantle' ||
        publication === 'melville'
    ) {
        return { section: 'South', color: colors.perthnow.blueCerulean }
    }
    if (publication === 'mandurah-coastal-times') {
        return { section: 'Mandurah', color: colors.perthnow.greenTourmaline }
    }
    return { section: 'default', color: 'default' }
}

export function createPNRegionTitle(
    publication: PNRegionalSections | undefined,
): string {
    if (publication === 'mandurah-coastal-times') {
        return 'Mandurah'
    }
    if (publication === 'western-suburbs') {
        return 'the Western Suburbs'
    }
    if (publication === 'southern') {
        return 'South Perth'
    }
    if (publication === 'central') {
        return 'Central Perth'
    }
    if (publication) {
        return publication.charAt(0).toUpperCase() + publication.slice(1)
    }
    return ''
}

function getBannerImageBySection(
    section: ePaperRegions,
    device: 'mobile' | 'tablet' | 'desktop',
) {
    if (device === 'mobile') {
        return require(`./assets/${section}-mobile.jpg`)
    }
    if (device === 'tablet') {
        return require(`./assets/${section}-web.jpg`)
    }
    if (device === 'desktop') {
        return require(`./assets/${section}-web.jpg`)
    }
}

export interface ePaperPageProps {
    latestEdition: Issue
    allEditions: Issue[]
    onEvent: (event: any) => void
    regionalSection?: PNRegionalSections
}

export const EPaperPage: React.FC<ePaperPageProps> = (props) => {
    const isEntitled = entitledToViewContent(
        {
            level: 'subscriber',
        },
        useSelector((state: AppState) => state.authentication),
    )

    const { onSubscribeClick } = useAuthActions()

    const section = useSection()
    const product = useProduct()
    const theme = useTheme()
    const sectionInfo: { section: ePaperRegions; color: string } =
        selectPNRegion(props.regionalSection)
    const today = new Date()
    return (
        <React.Fragment>
            {theme.kind === 'perthnow' && (
                <StyledEPaperLink
                    rel={'All Suburbs'}
                    to={'/epaper'}
                    isPrimary={true}
                >
                    <StyledEPaperArrow />
                    All Suburbs
                </StyledEPaperLink>
            )}
            <StyledEPaperHeadingWrapper>
                <StyledEPaperHeadingToday>
                    {section === 'default' && product === 'thewest'
                        ? 'Todays'
                        : product === 'perthnow'
                        ? ` ${sectionInfo.section} edition`
                        : 'Discover your local'}
                </StyledEPaperHeadingToday>
                <StyledEPaperLogoWrapper>
                    <div aria-hidden={true} style={{ paddingTop: calcRem(10) }}>
                        <StyledEPaperLogo />
                    </div>
                    <StyledEPaperHeadingEpaper>
                        ePaper
                    </StyledEPaperHeadingEpaper>
                </StyledEPaperLogoWrapper>
            </StyledEPaperHeadingWrapper>
            {theme.kind === 'perthnow' && (
                <div style={{ paddingBottom: calcRem(16) }}>
                    Printed weekly on{' '}
                    {props.regionalSection === 'mandurah-coastal-times'
                        ? ' Wednesday'
                        : ' Thursday'}
                </div>
            )}
            {!isEntitled &&
            PremiumPublicationsSet[props.latestEdition.publicationName] ? (
                <StyledEpaperUnsubscribedBanner
                    bannerUrls={{
                        mobile: require('./assets/unsubscribed/img-epaper-bg-UNSUB-S.jpg'),
                        tablet: require('./assets/unsubscribed/img-epaper-bg-UNSUB-M.jpg'),
                        desktop: require('./assets/unsubscribed/img-epaper-bg-UNSUB-L.jpg'),
                    }}
                    backgroundGradient={
                        'linear-gradient(91deg, rgba(0, 12, 35, 0.8) 0.63%, rgba(0, 0, 0, 0) 126.65%)'
                    }
                >
                    <StyledEpaperSubscribeText>
                        Read the paper, as it was printed
                    </StyledEpaperSubscribeText>
                    <StyledEpaperSubscribeUnderlineText>
                        $1 per day
                    </StyledEpaperSubscribeUnderlineText>
                    <StyledEpaperSubscribeButton to={`/epaper/subscribe`}>
                        Subscribe now
                        <StyledEPaperChevon />
                    </StyledEpaperSubscribeButton>
                </StyledEpaperUnsubscribedBanner>
            ) : (
                <StyledEPaperBanner
                    bannerUrls={{
                        desktop: getBannerImageBySection(
                            sectionInfo.section,
                            'desktop',
                        ),
                        mobile: getBannerImageBySection(
                            sectionInfo.section,
                            'mobile',
                        ),
                        tablet: getBannerImageBySection(
                            sectionInfo.section,
                            'tablet',
                        ),
                    }}
                >
                    <StyledEPaperBannerWrapper>
                        <StyledEPaperDateTime>
                            {product === 'thewest' &&
                            section != 'default' &&
                            props.latestEdition.date.getDate() -
                                today.getDate() ===
                                0
                                ? "Today's paper"
                                : product === 'thewest'
                                ? props.latestEdition?.date?.toLocaleDateString(
                                      'en-AU',
                                      {
                                          weekday: 'long',
                                          day: 'numeric',
                                          month: 'short',
                                      },
                                  )
                                : `The latest from ${createPNRegionTitle(
                                      props.regionalSection,
                                  )}`}
                        </StyledEPaperDateTime>
                        <StyledEPaperSubheading>
                            As it was printed
                        </StyledEPaperSubheading>
                        {theme.kind === 'perthnow' && (
                            <StyledEPaperPNButton
                                to={props.latestEdition?.url}
                                color={sectionInfo.color}
                                onClick={() =>
                                    window.open(
                                        props.latestEdition?.url,
                                        '_blank',
                                    )
                                }
                            >
                                Read now{' '}
                                <StyledEPaperChevon aria-hidden={true} />
                            </StyledEPaperPNButton>
                        )}
                        {theme.kind === 'thewest' && (
                            <StyledEPaperButton
                                to={props.latestEdition?.url}
                                onClick={() =>
                                    window.open(
                                        props.latestEdition?.url,
                                        '_blank',
                                    )
                                }
                            >
                                Read now{' '}
                                <span aria-hidden={true}>
                                    <StyledEPaperChevon />
                                </span>
                            </StyledEPaperButton>
                        )}
                    </StyledEPaperBannerWrapper>
                    <StyledEPaperCoverLeft
                        src={props.latestEdition?.coverUrl}
                        draggable={false}
                        aria-hidden={true}
                        alt={''}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = require('./assets/paper.jpg')
                        }}
                    />
                    <StyledEPaperCoverRight
                        draggable={false}
                        src={props.latestEdition?.coverUrl}
                        aria-hidden={true}
                        alt={''}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = require('./assets/paper.jpg')
                        }}
                    />
                    {theme.kind === 'thewest' && (
                        <React.Fragment>
                            <StyledEPaperCoffee
                                src={require('./assets/coffee.png')}
                                draggable={false}
                                aria-hidden={true}
                                alt={''}
                            />
                            <StyledEPaperNewspaper
                                src={require('./assets/newspaper.png')}
                                draggable={false}
                                aria-hidden={true}
                                alt={''}
                            />
                        </React.Fragment>
                    )}
                </StyledEPaperBanner>
            )}
        </React.Fragment>
    )
}

EPaperPage.displayName = 'ePaperPage'

export default EPaperPage
